import { Form, Input, Button, Tabs, Switch, Select, Spin, InputNumber, Space, Card, Divider } from "antd";
import { useState, useEffect, lazy, Suspense } from "react";
import {
   Site, DeviceData, DeviceProfile, InOut,
} from "../../model/model";
import { onFinishFailed } from "../helpers";
import SessionStore from "../../model/SessionStore";

// Lazily load device config forms so they are only bundled when needed.
const TtnDeviceConfForm = lazy(() => import("../../plug/io/ttn/DeviceConfForm"));
const ChirpStackDeviceConfForm = lazy(() => import("../../plug/io/chirpstack/DeviceConfForm"));

interface IProps {
  site: Site;
  initialValues: DeviceData;
  onFinish: (obj: DeviceData) => void;
  update?: boolean;
}

/**
 * Parses the `config` JSON string stored on a {@link DeviceData} into an object.
 * Returns an empty object on any parse error.
 */
function parseConfig(config: string | undefined | null): Record<string, unknown> {
  if (!config) return {};
  try {
    return JSON.parse(config);
  } catch {
    return {};
  }
}

function DeviceForm(props: IProps) {
  const [form] = Form.useForm();
  const [devProfiles, setDeviceProfile] = useState<DeviceProfile[]>([]);
  const [ioType, setIoType] = useState<string>('');
  const [configObj, setConfigObj] = useState<Record<string, unknown>>(
    parseConfig(props.initialValues.config),
  );
  const [coils, setCoils] = useState<string[]>(() => {
    const raw = props.initialValues.coils;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
    }
    return [];
  });

  useEffect(() => {
    SessionStore.getClient().getAllDeviceProfile(SessionStore.getBearer()).then((resp) => {
      setDeviceProfile(resp.data);
    });
  }, []);

  // Resolve ioType from the initially selected devProfileId.
  useEffect(() => {
    const profileId = props.initialValues.devProfileId;
    if (!profileId) return;
    resolveIoTypeFromProfile(profileId);
  }, [props.initialValues.devProfileId]);

  const resolveIoTypeFromProfile = (profileId: string) => {
    const profile = devProfiles.find((p) => p.id === profileId);
    if (profile) {
      fetchIoType(profile.ioId);
    } else {
      // Profile not yet loaded – fetch it directly.
      SessionStore.getClient()
        .getDeviceProfile(profileId, SessionStore.getBearer())
        .then((resp) => {
          const p: DeviceProfile = resp.data;
          fetchIoType(p?.ioId ?? '');
        })
        .catch(() => setIoType(''));
    }
  };

  const fetchIoType = (ioId: string) => {
    if (!ioId) { setIoType(''); return; }
    SessionStore.getClient()
      .getInOut(ioId, SessionStore.getBearer())
      .then((resp) => {
        const io: InOut = resp.data;
        setIoType(io?.type ?? '');
      })
      .catch(() => setIoType(''));
  };

  const onValuesChange = (changed: Partial<DeviceData>) => {
    if ('devProfileId' in changed && changed.devProfileId) {
      resolveIoTypeFromProfile(changed.devProfileId);
      // Generate default coil names when profile is selected (only on create).
      if (!props.update) {
        const profile = devProfiles.find((p) => p.id === changed.devProfileId);
        if (profile) {
          const count = profile.coilsCount ?? 0;
          setCoils(Array.from({ length: count }, (_, i) => `Switch${i}`));
        }
      }
    }
  };

  const onFinish = (values: DeviceData) => {
    const v = Object.assign(props.initialValues, values);

    const d: DeviceData = {
      id: v.id,
      topic: 'root.' + props.site.subId + '.' + props.site.orgId + '.' + props.site.id + '.' + v.id,
      subId: props.site.subId,
      orgId: props.site.orgId,
      siteId: props.site.id,
      name: v.name,
      devProfileId: v.devProfileId,
      ioId: "",
      gwDevId: v.gwDevId,
      descr: v.descr,
      expires: 0,
      locLat: 0,
      locLong: 0,
      recfreq: 0,
      config: JSON.stringify(configObj),
      decoderConf: "",
      online: false,
      lastSeenAt: 0,
      coils: coils as any,
    };

    props.onFinish(d);
  };

  const updateCoil = (index: number, value: string) => {
    const updated = [...coils];
    updated[index] = value;
    setCoils(updated);
  };

  const profilesOptions = devProfiles.map((r) => (
    <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>
  ));

  return (
    <Form
      layout="vertical"
      initialValues={props.initialValues}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      form={form}
    >
      <Tabs>
        <Tabs.TabPane tab="Device" key="name">
          <Form.Item label="Id" name="id" rules={[{ required: true, message: "Please enter a Id!" }]}>
            <Input disabled={props.update}/>
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a name!" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Gate" name="gwDevId" rules={[{ required: false }]}  >
            <Input/>
          </Form.Item>
          <Form.Item label="Description" name="descr">
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Profile" name="devProfileId" rules={[{ required: true, message: "Please select a deviceData profile!" }]}>
            <Select>{profilesOptions}</Select>
          </Form.Item>
          <Form.Item
                label="Device is disabled"
                name="isDisabled"
                valuePropName="checked"
                tooltip="Received uplink frames and join-requests will be ignored."
              >
                <Switch />
          </Form.Item>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Coils" key="coils">
          {coils.length === 0 ? (
            <p>Select a profile with coilsCount &gt; 0 to configure coil names.</p>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {coils.map((coil, i) => (
                <Card key={i} size="small">
                  <Space>
                    <InputNumber value={i} disabled min={0} style={{ width: 70 }} />
                    <Input
                      value={coil}
                      onChange={(e) => updateCoil(i, e.target.value)}
                      placeholder={`Coil ${i} name`}
                      style={{ width: 200 }}
                    />
                  </Space>
                </Card>
              ))}
            </Space>
          )}
        </Tabs.TabPane>

        {ioType === 'TTN' && (
          <Tabs.TabPane tab="TTN Config" key="ttn-dev">
            <Suspense fallback={<Spin />}>
              <TtnDeviceConfForm
                value={configObj as any}
                onChange={(cfg) => setConfigObj(cfg as unknown as Record<string, unknown>)}
              />
            </Suspense>
          </Tabs.TabPane>
        )}

        {ioType === 'CHIRPSTACK' && (
          <Tabs.TabPane tab="ChirpStack Config" key="chirp-dev">
            <Suspense fallback={<Spin />}>
              <ChirpStackDeviceConfForm
                value={configObj as any}
                onChange={(cfg) => setConfigObj(cfg as unknown as Record<string, unknown>)}
              />
            </Suspense>
          </Tabs.TabPane>
        )}
      </Tabs>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default DeviceForm;
